'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { Button } from './ui/button';
import { Menu, X, LogOut, ChevronDown } from 'lucide-react';
import { useSession, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { toast } from './toast';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const navItems = [
  { name: 'Home', href: '/' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'About Us', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

export const logoSrc = '/images/logo.png';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();

  const isLoggedIn = !!session;

  const handleSignOut = async () => {
    try {
      await signOut({ redirect: false });
      router.push('/login');
      toast({
        type: 'success',
        description: 'Signed out successfully',
      });
    } catch (error) {
      console.error('Sign-out error:', error);
      toast({
        type: 'error',
        description: 'Failed to sign out. Please try again.',
      });
    }
  };

  return (
    <header className="w-full h-[72px] z-50 border-b border-primaryBorder flex items-center justify-center">
      <div className="w-full flex items-center justify-between max-w-[1236px] px-5 mx-auto">
        {/* Logo */}
        <Link href="/" className="flex items-center" passHref>
          <Image
            src={logoSrc}
            alt="Logo"
            width={179.65}
            height={40}
            className="tracking-[0.15px]"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-12">
          <ul className="flex items-center gap-12">
            {navItems.map((item, i) => (
              <li key={i}>
                <Link
                  href={item.href}
                  className="text-base tracking-[0.5px] leading-6 text-[#171717] font-normal"
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>

          {isLoggedIn ? (
            <div className="flex items-center gap-4">
              <Link href="/chats/123" passHref>
                <Button
                  className="h-12 px-5 py-3 rounded-full font-semibold"
                  variant="gradient"
                >
                  Dashboard
                </Button>
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="px-3.5 py-1.5 gap-2 flex items-center bg-[#f5f5f5] rounded-[8px] hover:bg-[#eeeeee] transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                  >
                    <Avatar className="size-8">
                      <AvatarImage
                        src={(session?.user as any)?.image ?? undefined}
                        className="size-8"
                      />
                      <AvatarFallback className="bg-primary-gradient text-white">
                        {session?.user?.name?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-medium font-dmSans text-sm text-[#262626] leading-none">
                      {session?.user?.name?.split(' ')[0] || 'User'}
                    </p>
                    <ChevronDown color="#737373" size={20} />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-white border border-gray-200 shadow-lg"
                >
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-[#262626]">
                      {session?.user?.name || 'User'}
                    </p>
                    <p className="text-xs text-[#737373] mt-0.5">
                      {session?.user?.email || 'No email'}
                    </p>
                  </div>

                  <DropdownMenuSeparator className="bg-gray-100" />

                  <DropdownMenuItem
                    className="cursor-pointer text-red-600 hover:bg-red-50 hover:text-red-700 focus:bg-red-50 focus:text-red-700 px-3 py-2 transition-colors duration-150"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 size-4 transition-colors duration-150" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Link href="/login" passHref>
              <Button
                className="h-12 px-5 py-3 rounded-full font-semibold"
                variant="gradient"
              >
                Get Started
              </Button>
            </Link>
          )}
        </nav>

        {/* Mobile Hamburger Icon */}
        <button
          className="md:hidden text-[#171717] z-50"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Mobile Menu */}
        <div
          className={`md:hidden fixed top-0 left-0 size-full bg-white z-40 transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-6">
            <ul className="flex flex-col gap-6 mt-12">
              {navItems.map((item, i) => (
                <li key={i}>
                  <Link
                    href={item.href}
                    onClick={() => setIsOpen(false)}
                    className="text-lg font-medium text-[#171717]"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            {isLoggedIn ? (
              <div className="mt-8 space-y-4">
                <Link href="/chats/123" passHref>
                  <Button
                    variant="gradient"
                    className="w-full rounded-full bg-primary-gradient text-white"
                    onClick={() => setIsOpen(false)}
                  >
                    Dashboard
                  </Button>
                </Link>

                <button
                  onClick={() => {
                    handleSignOut();
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <LogOut className="size-4" />
                  <span>Sign out</span>
                </button>
              </div>
            ) : (
              <Link href="/login" passHref>
                <Button
                  variant="gradient"
                  className="mt-8 w-full rounded-full bg-primary-gradient text-white"
                  onClick={() => setIsOpen(false)}
                >
                  Get Started
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
